import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ROLE_LABELS } from "@/lib/license";

interface ProfileFormProps {
  fullName: string;
  company: string;
  email: string;
  role: string;
  createdAt: string;
  saving: boolean;
  onSave: (fullName: string, company: string) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

export default function ProfileForm({
  fullName,
  company,
  email,
  role,
  createdAt,
  saving,
  onSave,
  notify,
}: ProfileFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(fullName);
  const [org, setOrg] = useState(company);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(fullName);
    setOrg(company);
  }, [fullName, company]);

  const dirty = name.trim() !== (fullName ?? "").trim() || org.trim() !== (company ?? "").trim();

  const roleLabel = role === "admin" ? t("settings.roleAdmin") : role === "customer" ? t("settings.roleCustomer") : ROLE_LABELS[role] ?? role;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError(t("settings.profileNameRequired"));
      return;
    }
    setError(null);
    try {
      await onSave(name, org);
      notify(t("settings.profileUpdated"), "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : t("settings.saveFailed"), "error");
    }
  };

  const initials = (name || email || "U").slice(0, 1).toUpperCase();

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 font-heading text-xl font-semibold text-background-50">
          {initials}
        </span>
        <div className="min-w-0">
          <p className="truncate font-heading text-base font-semibold text-foreground-950">
            {name || t("settings.profileUnnamed")}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <span className="truncate text-foreground-500">{email}</span>
            <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-medium text-secondary-900">
              {roleLabel}
            </span>
          </div>
        </div>
      </div>

      <h2 className="mt-6 font-heading text-sm font-semibold text-foreground-950">{t("settings.profileBasic")}</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
          {t("settings.profileName")}
          <input
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("settings.profileNamePlaceholder")}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
          {t("settings.profileCompany")}
          <input
            type="text"
            value={org}
            onChange={(event) => setOrg(event.target.value)}
            placeholder={t("settings.profileCompanyPlaceholder")}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
          {t("settings.profileEmail")}
          <input
            type="text"
            value={email}
            readOnly
            className="cursor-not-allowed rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-500"
          />
        </label>
        <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
          {t("settings.profileCreated")}
          <input
            type="text"
            value={createdAt}
            readOnly
            className="cursor-not-allowed rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-500"
          />
        </label>
      </div>

      {error && <p className="mt-3 text-xs text-accent-700">{error}</p>}

      <div className="mt-5 flex items-center gap-3">
        <button
          type="submit"
          disabled={saving || !dirty}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? t("settings.profileSaving") : t("settings.profileSave")}
        </button>
        {dirty && <span className="text-xs text-foreground-500">{t("settings.profileUnsaved")}</span>}
      </div>
    </form>
  );
}